/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { useEffect } from "react"
import { REPORT_UPSTREAM_VERSION } from "../pcg-viewer/upstream-versions"

/**
 * Report interaction diagnostics (Medaica Report v0.6.1 at cardiac-panel-v4
 * git 4d1e5e6): a session-only log of what the reviewer did on the report
 * page, downloadable from the Report menu as JSON.
 *
 * - Kept in `sessionStorage` under `medaica-report-action-log-v1`, at most
 *   the latest 300 entries; nothing is sent anywhere. Where session storage
 *   is unavailable the log lives in memory for the page's lifetime.
 * - Clicks on buttons, menu items and links, and changes of inputs and
 *   selects, record only a descriptor of the control (tag, id, name, role,
 *   input type, aria-label or `data-action`), plus a checkbox or radio's
 *   checked state and a range input's position — never a field's value, so
 *   entered text is not recorded (text areas log nothing at all).
 * - Runtime errors and unhandled rejections are recorded with their message.
 */

export const REPORT_ACTION_LOG_KEY = "medaica-report-action-log-v1"
export const REPORT_ACTION_LOG_LIMIT = 300
const APP_NAME = "Medaica Report"

export interface ReportActionLogEntry {
  timestamp: string
  app: string
  version: string
  action: string
  detail: Record<string, unknown>
}

let memoryLog: ReportActionLogEntry[] = []

const sessionStore = (): Storage | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** The session's entries, oldest first (the latest 300). */
export function loadReportActionLog(): ReportActionLogEntry[] {
  const store = sessionStore()
  if (!store) return memoryLog.slice(-REPORT_ACTION_LOG_LIMIT)
  try {
    const parsed: unknown = JSON.parse(store.getItem(REPORT_ACTION_LOG_KEY) || "[]")
    return Array.isArray(parsed) ? (parsed as ReportActionLogEntry[]).slice(-REPORT_ACTION_LOG_LIMIT) : []
  } catch {
    return []
  }
}

const saveReportActionLog = (log: ReportActionLogEntry[]): void => {
  memoryLog = log
  try {
    sessionStore()?.setItem(REPORT_ACTION_LOG_KEY, JSON.stringify(log))
  } catch {
    // A full or blocked store keeps the in-memory copy only
  }
}

/** Appends one entry (upstream logReportAction) and returns it. */
export function logReportAction(action: string, detail: Record<string, unknown> = {}): ReportActionLogEntry {
  const entry: ReportActionLogEntry = {
    timestamp: new Date().toISOString(),
    app: APP_NAME,
    version: REPORT_UPSTREAM_VERSION.displayVersion,
    action: String(action || "unknown"),
    detail,
  }
  const log = [...loadReportActionLog(), entry]
  saveReportActionLog(log.slice(-REPORT_ACTION_LOG_LIMIT))
  return entry
}

/** The control an event came from, described without its content (upstream reportActionTarget). */
export function reportActionTarget(element: Element): Record<string, string | undefined> {
  const target = element.closest('button, [role="button"], [role="menuitem"], input, select, textarea, a') || element
  const inputType = target instanceof HTMLInputElement ? String(target.type || "").toLowerCase() : ""
  return {
    tag: target.tagName.toLowerCase(),
    id: target.id || undefined,
    name: target.getAttribute("name") || undefined,
    role: target.getAttribute("role") || undefined,
    type: inputType || undefined,
    actionLabel:
      target.getAttribute("aria-label") ||
      (target instanceof HTMLElement ? target.dataset.action : undefined) ||
      undefined,
  }
}

const onDocumentClick = (event: MouseEvent): void => {
  const target =
    event.target instanceof Element ? event.target.closest('button, [role="button"], [role="menuitem"], a') : null
  if (target) logReportAction("ui_click", reportActionTarget(target))
}

const onDocumentChange = (event: Event): void => {
  const target = event.target
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return
  const detail: Record<string, unknown> = reportActionTarget(target)
  if (target instanceof HTMLInputElement && ["checkbox", "radio"].includes(target.type)) detail.checked = target.checked
  if (target instanceof HTMLInputElement && target.type === "range") detail.rangePosition = target.value
  logReportAction("ui_change", detail)
}

const onWindowError = (event: ErrorEvent): void => {
  logReportAction("runtime_error", {
    message: String(event.message || "Unknown error"),
    source: event.filename || undefined,
    line: event.lineno || undefined,
    column: event.colno || undefined,
  })
}

const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
  const reason = event.reason as { message?: unknown } | undefined
  logReportAction("unhandled_rejection", { message: String(reason?.message || event.reason || "Unknown rejection") })
}

/**
 * Records the page's interactions while the report is mounted: "page_loaded"
 * once, then clicks, changes and runtime errors anywhere on the page
 * (dialogs included, as upstream listens on the whole document).
 */
export function useReportActionLog(): void {
  useEffect(() => {
    logReportAction("page_loaded", { path: window.location.pathname })
    document.addEventListener("click", onDocumentClick, true)
    document.addEventListener("change", onDocumentChange, true)
    window.addEventListener("error", onWindowError)
    window.addEventListener("unhandledrejection", onUnhandledRejection)
    return () => {
      document.removeEventListener("click", onDocumentClick, true)
      document.removeEventListener("change", onDocumentChange, true)
      window.removeEventListener("error", onWindowError)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
    }
  }, [])
}

/** Downloads the session's log as JSON (upstream downloadReportDiagnosticLog). */
export function downloadReportDiagnosticLog(): void {
  const events = loadReportActionLog()
  logReportAction("diagnostic_log_downloaded", { eventCount: events.length })
  const payload = {
    generatedAt: new Date().toISOString(),
    app: APP_NAME,
    version: REPORT_UPSTREAM_VERSION.displayVersion,
    note: "Session-only interaction diagnostics. Patient-entered text is intentionally excluded.",
    events: loadReportActionLog(),
  }
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `medaica-report-${REPORT_UPSTREAM_VERSION.displayVersion}-diagnostic-${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.json`
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
